import { NgClass } from '@angular/common';
import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { IconComponent } from 'components/layout/icon/icon.component';
import { LogEntryStatus } from 'constants/LogEntryStatus';
import { NotificationStatus } from 'enums/NotificationStatus';
import { TaskActionType } from 'enums/TaskActionType';
import { LogEntryMailModel } from 'models/generated/Communication.Api.Models.LogEntryAdditionalData.LogEntryMail';
import { NotificationModel } from 'models/generated/Communication.Api.Models.Notification';
import { TaskActionModel } from 'models/generated/Entities.GormCrm.TaskAction';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { UserPipe } from 'pipes/user.pipe';
import { Subscription } from 'rxjs';
import { NotificationService } from 'services/notification.service';
import { PushService } from 'services/push.service';
import { TaskService } from 'services/task.service';
import { UserService } from 'services/user.service';

@Component({
	selector: 'notifications',
	templateUrl: './notifications.component.html',
	styleUrls: ['./notifications.component.scss'],
	imports: [IconComponent, NgClass, TranslatePipe, CustomDatePipe, UserPipe]
})
export class NotificationsComponent implements OnDestroy {

	@Output() onClose = new EventEmitter();
	@Output() notificationsChanged = new EventEmitter<NotificationModel[]>();

	notificationStatus = NotificationStatus;
	notifications: NotificationModel[] = [];

	private onNotificationChanges: Subscription;
	private taskActions = new Map<string, TaskActionModel>();

	constructor(
		private translate: TranslatePipe,
		private pushService: PushService,
		private taskService: TaskService,
		private notificationService: NotificationService,
		public userService: UserService) {

		this.updateNotifications();
		this.onNotificationChanges = this.pushService.onNotificationChanges.subscribe(event => {
			const currentUserId = this.userService.getCurrentUser().id;
			if (event.data.userId === currentUserId) this.updateNotifications();
		});
	}

	async updateNotifications() {
		const notifications = await this.notificationService.getActiveNotifications();
		await this.loadMissingTaskActions(notifications.filter(n => n.communicationLogEntry?.taskActionId).map(n => n.communicationLogEntry?.taskActionId!));

		for (const notification of notifications) {
			if (!notification.communicationLogEntry) continue;

			const taskAction = this.taskActions.get(notification.communicationLogEntry.taskActionId ?? '');
			if (taskAction && taskAction.taskflow && taskAction.taskflow.sourceTemplate) {
				notification.breadcrumb = taskAction.taskflow.sourceTemplate.title + ' - ' + this.translate.transform('enum.task-action-type.' + TaskActionType[taskAction.type]);
			}
			notification.message = notification.communicationLogEntry.comment;
			if (notification.communicationLogEntry.statusType == LogEntryStatus.mailReceived) {
				const mailData = JSON.parse(notification.communicationLogEntry.additionalData) as LogEntryMailModel;
				notification.message = mailData.subject;
			}
		}

		this.notifications = notifications;
		this.notificationsChanged.emit(notifications);
	}

	async loadMissingTaskActions(taskActionIds: string[]) {
		const missingTaskActionIds = taskActionIds.filter(id => !this.taskActions.has(id));
		if (missingTaskActionIds.length > 0) {
			const taskActions = await this.taskService.getTaskActions(missingTaskActionIds);
			for (const taskAction of taskActions) {
				this.taskActions.set(taskAction.id!, taskAction);
			}
		}
	}

	notificationClicked(notification: NotificationModel) {
		if (this.notificationService.navigateToNotificationSource(notification)) {
			this.onClose.emit(); // If succesfully navigated, close the notification popup. For notifications with no navigation source, keep the popup open.
		}
	}

	deleteNotification(notification: NotificationModel) {
		notification.status = NotificationStatus.Deleted;
		this.notificationService.updateNotificationStatus(notification.id!, NotificationStatus.Deleted);
		this.notificationsChanged.emit(this.notifications);
		this.notifications.splice(this.notifications.indexOf(notification), 1);
	}

	activeNotificationCount(): number | null {
		const newNotifications = this.notifications.filter(notification => notification.status == NotificationStatus.New).length;
		return newNotifications > 0 ? newNotifications : null;
	}

	ngOnDestroy() {
		this.onNotificationChanges.unsubscribe();
	}
}
